import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { useConfig } from '#/hooks/useConfig';
import { useResponsiveValue } from '#/hooks/useResponsiveValue';
import {
  BreakpointKeys,
  MenuBackgroundStyle,
  ScrollBehaviour,
} from '#/service/constants';
import { Path } from '#/service/routes';
import { useModalStore } from '#/store/modal/modal.store';
import { usePaywallStore } from '#/store/paywall/paywall.store';

import { FULL_PAGE_MODAL_ID } from '../Modal/FullPageModal';
import { HamburgerMenu } from './HamburgerMenu';
import { HeaderMode } from './Header.types';
import { Menu } from './Menu';
import { MenuProps } from './Menu.types';
import { getHardcodedLoginMenuItem, TOP_MENU_HEIGHT_PX } from './Menu.utils';
import { useCachedMenu } from './useCachedMenu';

interface UseHeaderParams {
  menu: MenuProps;
  cacheKey?: string;
  alwaysCollapsed?: boolean;
  shouldShowUnauthenticatedHomePage?: boolean;
  overrideMode?: HeaderMode;
}

interface ResponsiveProps {
  Component: FC<MenuProps>;
}

const breakpointMap = {
  [BreakpointKeys.XS]: { Component: HamburgerMenu },
  [BreakpointKeys.SM]: { Component: HamburgerMenu },
  [BreakpointKeys.MD]: { Component: HamburgerMenu },
  [BreakpointKeys.LG]: { Component: HamburgerMenu },
  [BreakpointKeys.XL]: { Component: Menu },
  [BreakpointKeys.XXL]: { Component: Menu },
};

// Routes that should display transparent header when unauthenticated home page is shown
const TRANSPARENT_HEADER_ROUTES = new Set([
  Path.Registration,
  Path.GiftRedeem,
  Path.GiftPurchase,
]);

/**
 * Computes menu items based on header mode and current route
 *
 * Rules (applied in order, later rules override earlier ones):
 * 1. Default: Show all menu items from config
 * 2. Empty mode: Show no menu items (clean header for special pages)
 * 3. Reduced mode OR Registration page (without paywall): Show only login link (overrides mode)
 *    - Registration page gets priority to ensure "Sign In" link is always visible during signup
 */
const getMenuItems = (
  cachedMenu: MenuProps,
  mode: HeaderMode,
  isPaywallShowing: boolean,
  isTransparentHeader: boolean
) => {
  // Start with all configured menu items (default)
  let mainMenuItems = cachedMenu.mainMenuItems;
  let linkMenuItems = cachedMenu.linkMenuItems;

  // Empty mode shows no menu items
  if (mode === HeaderMode.Empty || isTransparentHeader || isPaywallShowing) {
    mainMenuItems = [];
    linkMenuItems = [];
  }

  if (mode === HeaderMode.Reduced) {
    mainMenuItems = [];
    linkMenuItems = [getHardcodedLoginMenuItem()];
  }

  return {
    mainMenuItems,
    linkMenuItems,
  };
};

// Hook to detect and track full page modal element
const useModalElement = () => {
  const { isFullPageModalOpen } = useModalStore();
  const [modalElement, setModalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!isFullPageModalOpen) {
      setModalElement(null);

      return;
    }

    // Function to check and update modal element
    const updateModalElement = () => {
      const element = document.getElementById(FULL_PAGE_MODAL_ID);

      setModalElement((prev) => {
        // Only update if the element reference actually changed
        if (prev !== element) {
          return element;
        }

        return prev;
      });
    };

    // Initial check - element should be in DOM since FullPageModal uses useLayoutEffect
    updateModalElement();

    // Watch for DOM changes while modal is open (in case modal element gets replaced)
    const observer = new MutationObserver(() => {
      updateModalElement();
    });

    // Only observe when we know a modal should be present
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [isFullPageModalOpen]);

  return modalElement;
};

// Hook to handle scroll behavior for header
const useScrollBehavior = (
  menuBackgroundStyle: MenuBackgroundStyle,
  mode: HeaderMode,
  scrollBehaviour: ScrollBehaviour,
  isAlwaysCollapsed: boolean,
  modalElement: HTMLElement | null,
  isTransparentHeader: boolean
) => {
  const { pathname } = useLocation();
  const prevScrollY = useRef(0);

  const [isCollapsed, setIsCollapsed] = useState(
    menuBackgroundStyle === MenuBackgroundStyle.Opaque
  );

  const [isTransparent, setIsTransparent] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    setIsTransparent(isTransparentHeader);
  }, [isTransparentHeader]);

  const handleScrollBehavior = useCallback(
    (
      isScrolledDown: boolean,
      isScrollingDown: boolean,
      isOverThreshold: boolean,
      isCloseToTop: boolean
    ) => {
      if (
        menuBackgroundStyle === MenuBackgroundStyle.Gradient &&
        isCollapsed !== isScrolledDown
      ) {
        setIsCollapsed(isScrolledDown);
      }

      if (isTransparentHeader) {
        if (isScrollingDown) {
          setIsTransparent(false);
        } else if (isCloseToTop) {
          setIsTransparent(true);
        }
      } else {
        setIsTransparent(false);
      }

      const isScrollBehaviourHidden =
        scrollBehaviour === ScrollBehaviour.Hidden ||
        mode === HeaderMode.Reduced;

      const isHiddenNext = isScrollingDown && !isCloseToTop;

      if (
        isScrollBehaviourHidden &&
        !isAlwaysCollapsed &&
        isOverThreshold &&
        isHidden !== isHiddenNext
      ) {
        setIsHidden(isHiddenNext);

        if (isHiddenNext) {
          document.body.click();
        }
      }
    },
    [
      isAlwaysCollapsed,
      isCollapsed,
      isHidden,
      isTransparentHeader,
      menuBackgroundStyle,
      mode,
      scrollBehaviour,
    ]
  );

  // Reset hidden state on navigation so the header is always visible on a new page
  useEffect(() => {
    setIsHidden(false);
  }, [pathname]);

  // Reset transparency when at the top
  useEffect(() => {
    if (!isTransparentHeader) {
      return;
    }

    const scrollY = modalElement?.scrollTop ?? window.scrollY;
    const isAtTop = scrollY <= TOP_MENU_HEIGHT_PX;

    if (isAtTop) {
      setIsTransparent(true);
      prevScrollY.current = scrollY;
    }
  }, [pathname, modalElement, isTransparentHeader]);

  // Modal scroll tracking
  useEffect(() => {
    if (!modalElement) {
      return;
    }

    const handleModalScroll = () => {
      const currY = modalElement.scrollTop;
      const prevY = prevScrollY.current;

      handleScrollBehavior(
        currY > 0,
        currY > prevY,
        Math.abs(currY - prevY) >= 1,
        currY <= TOP_MENU_HEIGHT_PX
      );

      prevScrollY.current = currY;
    };

    modalElement.addEventListener('scroll', handleModalScroll);

    return () => {
      modalElement.removeEventListener('scroll', handleModalScroll);
    };
  }, [modalElement, handleScrollBehavior]);

  // Window scroll tracking
  useScrollPosition(
    ({ prevPos, currPos }) => {
      if (modalElement) {
        return;
      }

      handleScrollBehavior(
        currPos.y < 0,
        prevPos.y > currPos.y,
        Math.abs(prevPos.y - currPos.y) >= 1,
        currPos.y >= -TOP_MENU_HEIGHT_PX
      );
    },
    [modalElement, handleScrollBehavior]
  );

  useEffect(() => {
    setIsCollapsed(menuBackgroundStyle === MenuBackgroundStyle.Opaque);
  }, [menuBackgroundStyle]);

  return { isCollapsed, isTransparent, isHidden };
};

// Helper to compute header mode based on current state
const computeHeaderMode = (
  overrideMode: HeaderMode | undefined,
  isTransparentHeader: boolean,
  shouldShowUnauthenticatedHomePage: boolean,
  pathname: string,
  isPaywallShowing: boolean
): HeaderMode => {
  if (overrideMode) {
    return overrideMode;
  }

  if (isTransparentHeader) {
    const isRegistrationWithoutPaywall =
      pathname === Path.Registration && !isPaywallShowing;

    return isRegistrationWithoutPaywall ? HeaderMode.Reduced : HeaderMode.Empty;
  }

  if (shouldShowUnauthenticatedHomePage && pathname === Path.Home) {
    return HeaderMode.Reduced;
  }

  // Default: show all menu items
  return HeaderMode.Default;
};

export const useHeader = ({
  menu,
  cacheKey,
  alwaysCollapsed = false,
  shouldShowUnauthenticatedHomePage = false,
  overrideMode,
}: UseHeaderParams) => {
  const {
    theme: {
      menu: { background },
    },
    menusConfig: {
      style: { scrollBehaviour },
    },
  } = useConfig();
  const { Component } = useResponsiveValue<ResponsiveProps>(breakpointMap);
  const cachedMenu = useCachedMenu(menu, cacheKey);
  const { pathname } = useLocation();
  const { isShowing: isPaywallShowing } = usePaywallStore();
  const modalElement = useModalElement();

  const isAlwaysCollapsed = [
    Path.Registration,
    Path.Settings,
    Path.GiftPurchase,
    Path.GiftRedeem,
  ].some((path) => matchPath(path, pathname));

  const isTransparentHeader =
    TRANSPARENT_HEADER_ROUTES.has(pathname) ||
    (pathname === Path.Registration && !isPaywallShowing) ||
    !!modalElement;

  const mode = useMemo(
    () =>
      computeHeaderMode(
        overrideMode,
        isTransparentHeader,
        shouldShowUnauthenticatedHomePage,
        pathname,
        isPaywallShowing
      ),
    [
      shouldShowUnauthenticatedHomePage,
      isTransparentHeader,
      isPaywallShowing,
      pathname,
      overrideMode,
    ]
  );

  const menuBackgroundStyle = isAlwaysCollapsed
    ? MenuBackgroundStyle.Opaque
    : (background ?? MenuBackgroundStyle.Gradient);

  const { isCollapsed, isTransparent, isHidden } = useScrollBehavior(
    menuBackgroundStyle,
    mode,
    scrollBehaviour ?? ScrollBehaviour.Fixed,
    isAlwaysCollapsed,
    modalElement,
    isTransparentHeader
  );

  const menuProps = useMemo(() => {
    const menuItems = getMenuItems(
      cachedMenu,
      mode,
      isPaywallShowing,
      isTransparentHeader
    );

    return {
      ...cachedMenu,
      ...menuItems,
      isCollapsed: alwaysCollapsed || isCollapsed,
      isHidden,
      mode,
      isTransparent,
    };
  }, [
    alwaysCollapsed,
    cachedMenu,
    isCollapsed,
    isHidden,
    mode,
    isTransparent,
    isPaywallShowing,
    isTransparentHeader,
  ]);

  return {
    Component,
    menuProps,
  };
};
